import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { useStyles } from 'styles/material-styles/comparisonTabs'
import { GUIDANCE_OPTIONS } from '../constants'
import styles from './styles.module.scss'

export type ComparisonTab = 'requestedPrice' | 'approved' | 'guidance'

export type ApprovalType = string

export type ApprovalOption = {
  id: ApprovalType
  label: string
}

export type GuidanceType = 'expert' | 'typical' | 'floor' | 'top25' | 'median' | 'bottom25'

interface ComparisonTabsProps {
  activeTabs?: ComparisonTab[]
  onTabChange?: (activeTabs: ComparisonTab[]) => void
  selectedApprovalTypes?: ApprovalType[]
  selectedGuidanceTypes?: GuidanceType[]
  onApprovalTypesChange?: (types: ApprovalType[]) => void
  onGuidanceTypesChange?: (types: GuidanceType[]) => void
  approvalOptions?: ApprovalOption[]
  allowedGuidanceTypes?: GuidanceType[]
  colorMap?: Partial<Record<string, string>>
  isLimitReached?: boolean
}

function ComparisonTabs({
  activeTabs: controlledActiveTabs,
  onTabChange,
  selectedApprovalTypes: controlledApprovalTypes,
  onApprovalTypesChange,
  selectedGuidanceTypes: controlledGuidanceTypes,
  onGuidanceTypesChange,
  approvalOptions = [],
  allowedGuidanceTypes,
  colorMap,
  isLimitReached,
}: ComparisonTabsProps) {
  const { classes } = useStyles()
  const [internalActiveTabs, setInternalActiveTabs] = useState<ComparisonTab[]>([])
  const [internalApprovalTypes, setInternalApprovalTypes] = useState<ApprovalType[]>([])
  const [internalGuidanceTypes, setInternalGuidanceTypes] = useState<GuidanceType[]>([])
  const [isApprovalDropdownOpen, setIsApprovalDropdownOpen] = useState(false)
  const [isGuidanceDropdownOpen, setIsGuidanceDropdownOpen] = useState(false)
  const approvalDropdownRef = useRef<HTMLDivElement>(null)
  const guidanceDropdownRef = useRef<HTMLDivElement>(null)

  const activeTabs = controlledActiveTabs ?? internalActiveTabs
  const selectedApprovalTypes = controlledApprovalTypes ?? internalApprovalTypes
  const selectedGuidanceTypes = controlledGuidanceTypes ?? internalGuidanceTypes
  const guidanceOptions = allowedGuidanceTypes
    ? GUIDANCE_OPTIONS.filter((option) => allowedGuidanceTypes.includes(option._id))
    : GUIDANCE_OPTIONS
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        approvalDropdownRef.current &&
        !approvalDropdownRef.current.contains(event.target as Node)
      ) {
        setIsApprovalDropdownOpen(false)
      }
      if (
        guidanceDropdownRef.current &&
        !guidanceDropdownRef.current.contains(event.target as Node)
      ) {
        setIsGuidanceDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleTabToggle = (tab: ComparisonTab) => {
    if (tab === 'approved') {
      setIsApprovalDropdownOpen(!isApprovalDropdownOpen)
      return
    }
    if (tab === 'guidance') {
      setIsGuidanceDropdownOpen(!isGuidanceDropdownOpen)
      return
    }

    const newActiveTabs = activeTabs.includes(tab)
      ? activeTabs.filter((t) => t !== tab)
      : [...activeTabs, tab]
    if (onTabChange) {
      onTabChange(newActiveTabs)
    } else {
      setInternalActiveTabs(newActiveTabs)
    }
  }

  const handleApprovalTypeToggle = (type: ApprovalType) => {
    const newTypes = selectedApprovalTypes.includes(type)
      ? selectedApprovalTypes.filter((t) => t !== type)
      : [...selectedApprovalTypes, type]

    if (onApprovalTypesChange) {
      onApprovalTypesChange(newTypes)
    } else {
      setInternalApprovalTypes(newTypes)
    }
  }

  const handleGuidanceTypeToggle = (type: GuidanceType) => {
    const newTypes = selectedGuidanceTypes.includes(type)
      ? selectedGuidanceTypes.filter((t) => t !== type)
      : [...selectedGuidanceTypes, type]

    if (onGuidanceTypesChange) {
      onGuidanceTypesChange(newTypes)
    } else {
      setInternalGuidanceTypes(newTypes)
    }
  }

  return (
    <div className={classes.comparisonTabsContainer}>
      <div className={classes.comparisonTabWrapper}>
        <button
          className={clsx(classes.comparisonTab, classes.comparisonTabFirst, {
            active: activeTabs.includes('requestedPrice'),
          })}
          onClick={() => handleTabToggle('requestedPrice')}
          aria-pressed={activeTabs.includes('requestedPrice')}
          disabled={isLimitReached && !activeTabs.includes('requestedPrice')}
        >
          {colorMap?.requestedPrice && (
            <span
              className={styles.colorDot}
              style={{ backgroundColor: colorMap.requestedPrice }}
              aria-hidden="true"
            />
          )}
          Requested Price
        </button>
      </div>
      <div className={classes.comparisonTabWrapper} ref={approvalDropdownRef}>
        <button
          className={clsx(classes.comparisonTab, { active: activeTabs.includes('approved') })}
          onClick={() => handleTabToggle('approved')}
          aria-pressed={activeTabs.includes('approved')}
          aria-expanded={isApprovalDropdownOpen}
        >
          Approved
        </button>
        {isApprovalDropdownOpen && (
          <div className={classes.approvalDropdown} role="menu">
            {approvalOptions.map((option) => {
              const isChecked = selectedApprovalTypes.includes(option.id)
              const isDisabled = isLimitReached && !isChecked
              const optionColor = colorMap?.[option.id]

              return (
                <label
                  key={option.id}
                  className={clsx(classes.approvalOption, { [styles.optionDisabled]: isDisabled })}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => handleApprovalTypeToggle(option.id)}
                  />
                  {optionColor && (
                    <span
                      className={styles.colorDot}
                      style={{ backgroundColor: optionColor }}
                      aria-hidden="true"
                    />
                  )}
                  <span data-testid={`approval-${option.id}`}>{option.label}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>
      <div className={classes.comparisonTabWrapper} ref={guidanceDropdownRef}>
        <button
          className={clsx(classes.comparisonTab, classes.comparisonTabLast, {
            active: activeTabs.includes('guidance'),
          })}
          onClick={() => handleTabToggle('guidance')}
          aria-pressed={activeTabs.includes('guidance')}
          aria-expanded={isGuidanceDropdownOpen}
        >
          Guidance
        </button>
        {isGuidanceDropdownOpen && (
          <div className={classes.guidanceDropdown} role="menu">
            {guidanceOptions.map((option) => {
              const isChecked = selectedGuidanceTypes.includes(option._id)
              const isDisabled = isLimitReached && !isChecked
              const optionColor = colorMap?.[option._id]

              return (
                <label
                  key={option._id}
                  className={clsx(classes.guidanceOption, { [styles.optionDisabled]: isDisabled })}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => handleGuidanceTypeToggle(option._id)}
                  />
                  {optionColor && (
                    <span
                      className={styles.colorDot}
                      style={{ backgroundColor: optionColor }}
                      aria-hidden="true"
                    />
                  )}
                  <span>{option.label}</span>
                </label>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ComparisonTabs
